<script setup lang="ts">
import { useI18n } from '@/plugins/i18n'
import { useSearchStore } from './store/index.ts'
import { ref, onMounted, computed, watch } from 'vue'
import type { PropType } from 'vue'
import type { SearchFilterOption } from './types.ts';
import { apiGetFilterOptions } from './api.ts';
import SaiDropDown from '@/components/common/SaiDropDown/index.ce.vue';
import iconOpen from '@/assets/img/icon-search-all-filters-open.svg';
import iconClose from '@/assets/img/icon-search-all-filters-close.svg';
import iconSelected from '@/assets/img/icon-search-all-filters-option-selected.svg';

// emits
const emit = defineEmits(['updateSelectedItems']);

//props
const props = defineProps({
    className: { type: String, default: '', required: false },
    multiple: { type: Boolean,  default: false, required: false }, 
    name: { type: String,  default: '', required: true },
    placeholder: { type: String,  default: '', required: true },
    searchPlaceholder: { type: String, default: 'Search...', required: false },
    iconMain: { type: String, default: '', required: false },
    field: { type: String, default: '', required: true },
    lookupField: { type: String, default: '', required: false },
    initialOptions: { type:  Array as PropType<SearchFilterOption[]>, default: () => [], required: false }, //set initial options that you want in top of the option list
    defaultOptionLookupField: { type: String, default: '', required: false }, //set default options to fall back to, or to allways show options
    disabled: { type: Boolean, default: false, required: false },
    searchable: { type: Boolean, default: false },
    hasClear: { type: Boolean, default: true },
    tagging: { type: Boolean, default: false },
    useTeleport: { type: Boolean, default: false, required: false },
    closeOnClickOutside: { type: Boolean, default: false, required: false }, //for closing other dropdowns
    showSelectedInPlaceholder: { type: Boolean, default: false, required: false },
    loadOptionsOnClick: { type: Boolean, default: false, required: false }, //whether to load options dynamically,
    translate: { type: Boolean, default: false, required: false }, //whether to translate options text
});

//store
const searchStore = useSearchStore()
const { getItem } = searchStore

//computed 
const selectedValues = computed(() => searchStore[props.field]);

//ref
const isOptionsLoading = ref(false);
const isOptionsLoaded = ref(false);
const options = ref<SearchFilterOption[]>([]);

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance

// Lifecycle hooks
onMounted(() => {
    const tmpOptions: SearchFilterOption[] = searchStore[props.lookupField] || [];
    const tmpDefaultOptions: SearchFilterOption[] = searchStore[props.defaultOptionLookupField] || [];
    if((tmpOptions && tmpOptions.length) || (tmpDefaultOptions && tmpDefaultOptions.length)) { 
      options.value = getUniqueOptions(tmpOptions);
      // isOptionsLoaded.value = true;
    }
})

//methods
const getUniqueOptions = (options: SearchFilterOption[]): SearchFilterOption[] => {
  const uniqueById = (arr: SearchFilterOption[]) => {
    const map = new Map<string, SearchFilterOption>()
    arr.forEach(item => {
      if (!map.has(item.id.toString())) {
        map.set(item.id.toString(), item)
      }
    })
    return Array.from(map.values())
  }

  // Use props directly inside the function
  const initialOptions = uniqueById(props.initialOptions || [])
  let defaultOptions: SearchFilterOption[] = searchStore[props.defaultOptionLookupField] || [];
  defaultOptions = uniqueById([...defaultOptions]).filter(
    opt => !initialOptions.some(init => init.id.toString() === opt.id.toString())
  )
  //options has count, so we override the initial and default options with count from options
  const initialOptionsWithCount = options.filter(
    opt => [...initialOptions].some(item => item.id.toString() === opt.id.toString())
  )

  const initialOptionsWithoutCount = initialOptions.filter(
    opt => ![...initialOptionsWithCount].some(item => item.id.toString() === opt.id.toString())
  )
  //default options that are not in options(with count)
  const defaultsNotInOptions = defaultOptions.filter(
    opt => ![...options].some(item => item.id.toString() === opt.id.toString())
  )


  const filteredOptions = options.filter(
    opt => ![...initialOptionsWithCount].some(item => item.id.toString() === opt.id.toString())
  )
  
  return [...initialOptionsWithCount, ...initialOptionsWithoutCount, ...filteredOptions, ...defaultsNotInOptions]
}

//event handlers
const loadOptions = async () => {
   if(options.value.length == 0) {
        const tmpOptions: SearchFilterOption[] = searchStore[props.lookupField];
        options.value = getUniqueOptions(tmpOptions);
        //no options to load
        if(!isOptionsLoaded.value && options.value.length == 0 && props.loadOptionsOnClick) {
            isOptionsLoading.value = true;
            // @ts-ignore
            const tmpOptions = await apiGetFilterOptions(props.field);
            options.value = getUniqueOptions(tmpOptions);
            searchStore[props.lookupField] = tmpOptions;
            //if defaultOptionLookupField is set, and there is no options loaded yet
            if(props.defaultOptionLookupField && (!searchStore[props.defaultOptionLookupField] || (searchStore[props.defaultOptionLookupField] && searchStore[props.defaultOptionLookupField].length === 0))) {
                searchStore[props.defaultOptionLookupField] = tmpOptions;
            }
            isOptionsLoaded.value = true;
            isOptionsLoading.value = false;
            
        }
    }
}

const handleUpdateSelectedItems = (items: SearchFilterOption[]) => {
    searchStore.setItem(props.field,  items);
    emit('updateSelectedItems', {field: props.field, selectedItems: items});
}

const handleHeaderClick = async (show: Boolean) => {
    if(show) {
        await loadOptions();
    }
}

const handleClickOutside = () => {
    //console.log('click outside', props.field);
}

watch(
  () => searchStore[props.lookupField],  // Watch this specific lookup field
  (newVal) => {
    if (newVal) {
      options.value = getUniqueOptions(newVal)
    }
  },
  { deep: true } // optional, if it's an array of objects
)



watch(
  () => searchStore[props.defaultOptionLookupField],  // Watch this specific lookup field
  (newVal) => {
    if (newVal) {
      options.value = getUniqueOptions(searchStore[props.lookupField])
    }
  },
  { deep: true } // optional, if it's an array of objects
)

</script>
<template>
    <SaiDropDown 
        :className="props.className"
        :placeholder="props.placeholder"
        :multiple="props.multiple"
        :name="props.name"
        :isLoading="isOptionsLoading"
        :searchable="props.searchable"
        :searchPlaceholder="props.searchPlaceholder"
        :hasClear="props.hasClear"
        :options="options"
        :value="selectedValues"
        :iconMain="props.iconMain"
        :iconOpen="iconOpen"
        :iconClose="iconClose"
        :iconSelected="iconSelected"
        @updateSelectedItems="handleUpdateSelectedItems"
        @handleHeaderClick ="handleHeaderClick"
        @handleClickOutside="handleClickOutside"
        :useTeleport="props.useTeleport"
        :closeOnClickOutside="props.closeOnClickOutside"
        :showSelectedInPlaceholder="props.showSelectedInPlaceholder"
        :disabled="props.disabled"
        :translate="translate"
    >
      <template #info>
        <slot name="info" />
      </template>
    </SaiDropDown>
</template>
